import clsx from 'clsx'
import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { EmptyState } from '../../../cc'
import type { ReportColumnDef } from '../../../cc/ReportTable.types'
import { TONE_BG, TONE_TEXT } from '../../../cc/tokens'
import type { ReportRow } from '../../../lib/types'
import { exportXlsx } from '../../useReportData'
import { reportCard, reportCardSub, reportCardTitle, reportLoading } from '../../ui'
import { clearNarrowing, isNarrowed, windowLabel, type AlertFilters } from '../alertFilters'
import { ALERT_LABELS, fmtDec, fmtInt, fmtIst, kindLabel, labelFor, severityTone } from '../alertLabels'
import type { ByLocationRow, FacetsEnvelope } from '../alertTypes'
import type { AnalyticsStatus } from '../useAlertAnalytics'

/**
 * The pieces every sub-tab of the alerts report shares, so the seven views read as one
 * report: the same card chrome, the same loading / error / empty vocabulary, the same
 * "Export this view" hand-off and the same cell renderers. Anything a view would
 * otherwise copy from a sibling belongs here.
 */

export interface ViewProps {
  filters: AlertFilters
  /** Partial update; the shell merges, resets `page` where needed and writes the URL. */
  setFilters: (patch: Partial<AlertFilters>) => void
  facets: FacetsEnvelope | null
}

/** The Locations tab's map slot: given the loaded bins and the report's filter handle,
 *  returns the map element. It is rendered inside a fixed-height card, so the element
 *  should fill its container (`h-full w-full`), not bring its own height. */
export type LocationsMapSlot = (
  rows: ByLocationRow[],
  ctx: Pick<ViewProps, 'filters' | 'setFilters'> & {
    /** Bubble click: list that hotspot's alerts under the map (toggle). */
    selectBin?: (row: Pick<ByLocationRow, 'place' | 'zone' | 'lat' | 'lon'>) => void
  },
) => ReactNode

/* ------------------------------------------------------------------- export slot */

export type ExportFn = () => Promise<void>

/** The filter bar owns the one "Export this view" button; the active view hands it the
 *  function to run through this context, and withdraws it on unmount. */
export const ExportContext = createContext<(fn: ExportFn | null) => void>(() => undefined)

export function useViewExport(fn: ExportFn | null): void {
  const register = useContext(ExportContext)
  useEffect(() => {
    register(fn)
    return () => register(null)
  }, [fn, register])
}

/** Build an export function for a view: human labels from ALERT_LABELS, the window as the
 *  sheet's subtitle, and every epoch-ms timestamp column pre-formatted as IST text --
 *  exportXlsx sends raw numbers as numbers, which for a timestamp would hand Excel a
 *  13-digit integer. */
export function makeExport<T extends object>(
  rows: T[],
  columns: string[],
  filename: string,
  title: string,
  filters: AlertFilters,
  tsColumns: readonly string[] = [],
): ExportFn | null {
  if (!rows.length) return null
  return () => exportXlsx(
    rows.map((r) => {
      const out: Record<string, unknown> = { ...(r as Record<string, unknown>) }
      for (const c of tsColumns) if (c in out) out[c] = fmtIst(out[c])
      return out
    }),
    columns,
    `${filename}-${filters.site}`,
    { title, subtitle: windowLabel(filters), labels: ALERT_LABELS },
  )
}

/* ------------------------------------------------------------------- card chrome */

export function ViewCard({
  title, sub, actions, children, className, bodyClassName,
}: {
  title: string
  sub?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}): JSX.Element {
  return (
    <section className={clsx(reportCard, 'overflow-hidden', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 px-5 py-3.5">
        <div className="min-w-0">
          <h3 className={reportCardTitle}>{title}</h3>
          {sub ? <p className={clsx('mt-0.5', reportCardSub)}>{sub}</p> : null}
        </div>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
      <div className={clsx('p-4', bodyClassName)}>{children}</div>
    </section>
  )
}

/* -------------------------------------------------------------- loading / empty */

export interface ViewStateProps {
  status: AnalyticsStatus
  error: string | null
  rowCount: number
  filters: AlertFilters
  setFilters: (patch: Partial<AlertFilters>) => void
  /** Copy for "the window genuinely has nothing", as opposed to "the filter removed it". */
  noData: { title: string; body: string }
  children: ReactNode
}

/** Wraps a view's loaded content with the shared loading / error / empty states. The two
 *  empty states are deliberately different: a filter that matched nothing offers a way
 *  out (clear it); a window with no data at all says so and does not pretend the filter
 *  is to blame. */
export function ViewState({ status, error, rowCount, filters, setFilters, noData, children }: ViewStateProps): JSX.Element {
  if (status === 'loading' || status === 'idle') {
    return <div className={reportLoading} aria-busy="true" aria-live="polite">Reading the alert record…</div>
  }
  if (status === 'error') {
    return (
      <div className={clsx(reportCard, 'p-4')} role="alert">
        <EmptyState title="The reporting database is not answering" body={error ?? 'Unknown error'} />
      </div>
    )
  }
  if (rowCount === 0) {
    const narrowed = isNarrowed(filters)
    return (
      <div className={clsx(reportCard, 'p-4')}>
        <EmptyState
          title={narrowed ? 'No alerts match these filters' : noData.title}
          body={narrowed ? 'Widen the date range, or clear the severity / type / driver / vehicle filters.' : noData.body}
        />
        {narrowed ? (
          <div className="flex justify-center pb-4">
            <button
              type="button"
              onClick={() => setFilters(clearNarrowing(filters))}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700 shadow-sm hover:border-slate-300 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
            >
              Clear filters
            </button>
          </div>
        ) : null}
      </div>
    )
  }
  return <>{children}</>
}

/* ------------------------------------------------------------- cell renderers */

/** Interfaces have no implicit index signature, so a typed row array is not a ReportRow[]
 *  to the compiler even though it is one at runtime -- this is the one sanctioned cast. */
export function asRows<T extends object>(rows: T[]): ReportRow[] {
  return rows as unknown as ReportRow[]
}

export function KindPill({ kind }: { kind: string | null | undefined }): JSX.Element {
  if (!kind) return <span className="text-slate-400">—</span>
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-medium text-slate-700 ring-1 ring-inset ring-slate-500/10" title={kind}>
      {kindLabel(kind)}
    </span>
  )
}

export function SeverityPill({ severity }: { severity: string | null | undefined }): JSX.Element {
  const tone = severityTone(severity)
  return (
    <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset ring-black/5', TONE_BG[tone], TONE_TEXT[tone])}>
      {severity ?? '—'}
    </span>
  )
}

const TS_DEF: Partial<ReportColumnDef> = {
  align: 'left',
  render: (v) => <span className="font-mono text-[12px] tabular-nums text-slate-700">{fmtIst(v)}</span>,
}

const INT_DEF: Partial<ReportColumnDef> = {
  align: 'right',
  render: (v) => <span className="font-mono tabular-nums">{fmtInt(typeof v === 'number' ? v : null)}</span>,
}

const CRIT_DEF: Partial<ReportColumnDef> = {
  align: 'right',
  render: (v) => {
    const n = typeof v === 'number' ? v : 0
    return (
      <span className={clsx('font-mono tabular-nums', n > 0 ? clsx('font-medium', TONE_TEXT.danger) : 'text-slate-400')}>
        {fmtInt(n)}
      </span>
    )
  },
}

const DEC_DEF: Partial<ReportColumnDef> = {
  align: 'right',
  render: (v) => (
    <span className={clsx('font-mono tabular-nums', v == null && 'text-slate-400')}>
      {fmtDec(typeof v === 'number' ? v : null)}
    </span>
  ),
}

const KIND_DEF: Partial<ReportColumnDef> = {
  render: (v) => <KindPill kind={typeof v === 'string' ? v : null} />,
}

/** Column definitions with the shared labels and renderers for whichever of the known
 *  columns the view lists; anything unknown falls through to ReportTable's defaults. */
export function columnDefs(columns: readonly string[], overrides: Record<string, Partial<ReportColumnDef>> = {}): Record<string, Partial<ReportColumnDef>> {
  const out: Record<string, Partial<ReportColumnDef>> = {}
  for (const c of columns) {
    let def: Partial<ReportColumnDef> = { label: labelFor(c) }
    if (c.endsWith('_ts') || c === 'ts' || c === 'acknowledged_at') def = { ...def, ...TS_DEF }
    else if (c === 'critical') def = { ...def, ...CRIT_DEF }
    else if (['alerts', 'warn', 'vehicles', 'drivers', 'kinds', 'night_alerts', 'occurrence_count', 'total_count'].includes(c)) def = { ...def, ...INT_DEF }
    else if (['km', 'alerts_per_100km', 'median_speed_kph', 'median_limit_kph', 'share_pct'].includes(c)) def = { ...def, ...DEC_DEF }
    else if (c === 'kind' || c === 'top_kind') def = { ...def, ...KIND_DEF }
    else if (c === 'severity') def = { ...def, render: (v) => <SeverityPill severity={typeof v === 'string' ? v : null} /> }
    out[c] = { ...def, ...overrides[c] }
  }
  return out
}

/** Small italic helper under a card title telling the reader what a row click does. */
export function ClickHint({ children }: { children: ReactNode }): JSX.Element {
  return <p className="px-1 pb-2 text-[11px] text-slate-400">{children}</p>
}
